import clsx from 'clsx';
import React from 'react';

import IconDeepResearchVertical from '@components/Images/DeepResearch/IconDeepResearchVertical';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import SuggestedAiPromptCard from '@components/SuggestedAiPromptCard';
import { SuggestedAiPromptCardAs } from '@components/SuggestedAiPromptCard/constants';
import { useEventTracking } from '@hooks/index';
import { useSuggestedAiPrompts } from '@hooks/useSuggestedAiPrompts';
import { deep_research_path } from '@routes';
import { IMGIX_URL } from '@src/env';
import { ClickTarget, click } from '@tracking';

interface DeepResearchSectionProps {
  className?: string;
  description?: string;
  isProSubscriberNotBot: boolean;
}

const DeepResearchSection = ({
  className,
  description,
  isProSubscriberNotBot,
}: DeepResearchSectionProps) => {
  const { Track, trackEvent } = useEventTracking();
  const { isLoadingSuggestedAiPrompts, suggestedAiPrompts } = useSuggestedAiPrompts();

  const handleLogoClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.DeepResearchLogo }));
    window.location.href = deep_research_path();
  };

  return (
    <Track>
      <div className={clsx('m-auto lg:w-[810px]', className)}>
        <div className="mt-56 flex flex-col gap-24 sm:mt-80 sm:gap-32 lg:mt-88 lg:flex-row lg:items-center lg:gap-48">
          <IconDeepResearchVertical
            role="button"
            aria-pressed="false"
            onClick={handleLogoClick}
            className="w-[182px] flex-shrink-0 cursor-pointer sm:w-[270px] lg:w-[310px]"
          />
          <p className="m-0 font-suisse-intl text-body-md sm:text-body-lg">
            {description ||
              'Accelerate your research with AI-powered answers grounded in over a decade of The Information’s trusted reporting, org charts and data &mdash; because AI is only as smart as the data it learns from.'}
          </p>
        </div>
        <video
          width="100%"
          height="auto"
          className="pointer-events-none mt-16 sm:mt-32 lg:px-16"
          aria-label="Example questions being entered into TI Deep Research"
          autoPlay
          disablePictureInPicture
          loop
          muted
          playsInline
        >
          <source
            src={`${IMGIX_URL}/global/deep_research/paywall-search-bar-mobile.mp4`}
            media="(max-width: 479px)"
          />
          <source
            src={`${IMGIX_URL}/global/deep_research/paywall-search-bar-v3.mp4`}
            media="(min-width: 480px)"
          />
        </video>
      </div>
      <h3 className=" mb-0 mt-8 font-suisse-intl-mono text-overline-md md:text-overline-lg lg:mt-16">
        Example prompts
      </h3>
      <div className="mt-16 grid grid-cols-1 gap-24 sm:grid-cols-2 lg:grid-cols-4">
        {isLoadingSuggestedAiPrompts ? (
          <div className="mx-auto mt-8 pt-16">
            <CustomSpinner size={10} color={CustomSpinnerColor.PrimaryMagenta} />
          </div>
        ) : (
          suggestedAiPrompts.map((suggestion, index) => (
            <SuggestedAiPromptCard
              key={index}
              as={SuggestedAiPromptCardAs.Anchor}
              suggestedAiPrompt={suggestion}
              disablePrompts={!isProSubscriberNotBot}
            />
          ))
        )}
      </div>
    </Track>
  );
};

export default DeepResearchSection;
